import { WelcomeBanner, StatTile, type BannerTone } from '../infrastructure/widgets/WelcomeBanner';
import { useCollapsedList, ShowAllButton } from '../infrastructure/widgets/DashboardKit';
import { Users as UsersIcon, Inbox as InboxIcon, Target as TargetIcon, FileText as FileTextIcon, Receipt as ReceiptIcon, Landmark as LandmarkIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Briefcase,
  TrendingUp,
  Phone,
  BarChart3,
  FileText,
  Radio,
  Truck,
  Clock,
  AlertTriangle,
  Inbox,
  CheckSquare,
  FilePen,
  FlaskConical,
  ChevronRight,
  ExternalLink,
  Sparkles,
  Package,
  Building,
  Layers,
} from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { formatINR } from '@/shared/utils/currency';
import { useRememberedState } from '@/shared/hooks/useRememberedFilters';
import { ROUTES } from '@/routes/paths';
import { useOwnerDashboard } from '../hooks/useOwnerDashboard';
import { useTeamAttendance } from '@/features/attendance/hooks/useAttendance';
import { formatClock, formatDuration, minutesOnDuty } from '@/features/attendance/utils';
import { SalesEmployeeDashboard } from './SalesEmployeeDashboard';
import { SalesAdminDashboard } from './SalesAdminDashboard';
import { EmployeePipelineMonitor, canMonitorPipeline } from '@/features/pipeline/components/EmployeePipelineMonitor';
import { usePipelineRepSummary } from '@/features/pipeline/hooks/usePipeline';
import { AsmTeamDsrCard } from '../widgets/sales-employee/AsmTeamDsrCard';
import { BdmDsrWidget } from '../widgets/sales-employee/BdmDsrWidget';
import { LiveFleetRadarPage } from '@/features/tracking/pages/LiveFleetRadarPage';
import { WidgetEmpty, WidgetError, WidgetLoading, WidgetPermissionDenied } from '../infrastructure/widgets/WidgetStates';

const dayKey = (d: Date) => d.toLocaleDateString('en-CA');

const PERIODS = [
  {
    key: 'today',
    label: 'Today',
    range: () => {
      const t = dayKey(new Date());
      return { from: t, to: t };
    },
  },
  {
    key: 'week',
    label: 'This Week',
    range: () => {
      const now = new Date();
      const start = new Date(now);
      start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      return { from: dayKey(start), to: dayKey(now) };
    },
  },
  {
    key: 'month',
    label: 'This Month',
    range: () => {
      const now = new Date();
      return { from: dayKey(new Date(now.getFullYear(), now.getMonth(), 1)), to: dayKey(now) };
    },
  },
  {
    key: 'last_month',
    label: 'Last Month',
    range: () => {
      const now = new Date();
      return {
        from: dayKey(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        to: dayKey(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    },
  },
];

const formatTimeAgo = (iso: string | null) => {
  if (!iso) return 'No recorded session';
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  return hours < 1 ? 'Active just now' : hours < 24 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
};

export function SurajMishraDashboard() {
  const user = useUser();

  // Primary mode: 'workforce' (All Personnel End-to-End Monitoring) or 'personal' (Lead BDM Direct Accounts)
  const [activeMode, setActiveMode] = useRememberedState<'workforce' | 'personal'>(
    'admin-command-dashboard-mode',
    'view',
    'workforce'
  );

  // Sub-navigation for workforce monitoring dimensions
  const [monitoringTab, setMonitoringTab] = useRememberedState<
    'scoreboard' | 'pipeline' | 'dsr' | 'dispatch' | 'commercial' | 'gps' | 'analytics'
  >('admin-workforce-monitoring-tab', 'tab', 'scoreboard');

  // Selected date range preset
  const [periodKey, setPeriodKey] = useState<string>('month');
  const activePeriod = PERIODS.find((p) => p.key === periodKey) ?? PERIODS[2];
  const range = useMemo(() => activePeriod.range(), [activePeriod]);

  // Selected representative for individual DSR drill-down
  const [selectedRep, setSelectedRep] = useState<{ id: string; name: string } | null>(null);

  // Central executive data query (authorized for General Manager & Sales Administrator)
  const { data: ownerData, isLoading: isDataLoading, isError: isDataError } = useOwnerDashboard(
    range.from,
    range.to
  );

  // Live real-time attendance
  const { data: attendanceList = [] } = useTeamAttendance(true);

  // Pipeline rep summary query
  const monitorAllowed = canMonitorPipeline(user?.role);
  const [monitorOwner, setMonitorOwner] = useState('');
  const { data: repSummary, isLoading: isRepSummaryLoading } = usePipelineRepSummary(
    { startDate: range.from, endDate: range.to },
    monitorAllowed
  );

  const teamMembers = ownerData?.team ?? [];
  const attention = ownerData?.attention;
  const dispatchMetrics = ownerData?.dispatch;
  const moneyMetrics = ownerData?.money;
  const leadMetrics = ownerData?.leads;
  const customerMetrics = ownerData?.customers;

  // Attendance aggregates
  const inDutyCount = attendanceList.filter((a) => a.check_in && !a.check_out).length;
  const checkedOutCount = attendanceList.filter((a) => a.check_out).length;
  const absentCount = attendanceList.filter((a) => !a.check_in).length;
  // In now first, then checked out, then not in — first rows open, rest on demand.
  const attendanceSorted = useMemo(() => [...attendanceList].sort((a, b) =>
    (a.check_in ? (a.check_out ? 1 : 0) : 2) - (b.check_in ? (b.check_out ? 1 : 0) : 2)), [attendanceList]);
  const attList = useCollapsedList(attendanceSorted, 8);

  // Critical operational attention triage list
  const operationalAlerts = useMemo(() => {
    if (!attention) return [];
    const list: {
      id: string;
      label: string;
      count: number;
      icon: typeof AlertTriangle;
      route: string;
      severity: 'critical' | 'warning' | 'info';
      urgencyCopy: string;
    }[] = [
      {
        id: 'overdue-followups',
        label: 'Overdue Client Follow-ups',
        count: attention.overdueFollowups ?? 0,
        icon: Clock,
        route: ROUTES.deals,
        severity: 'critical',
        urgencyCopy: 'Client communication SLA breached',
      },
      {
        id: 'overdue-dispatches',
        label: 'Overdue Order Dispatches',
        count: dispatchMetrics?.overdue.count ?? 0,
        icon: Truck,
        route: ROUTES.dispatchOrders,
        severity: 'critical',
        urgencyCopy: 'Shipment SLA exceeded standard window',
      },
      {
        id: 'unassigned-enquiries',
        label: 'Unassigned Inbound Inquiries',
        count: attention.enquiries ?? 0,
        icon: Inbox,
        route: ROUTES.leadInbox,
        severity: 'warning',
        urgencyCopy: 'Pending territory allocation',
      },
      {
        id: 'expiring-quotes',
        label: 'Quotations Expiring Within 4 Days',
        count: attention.quotationsExpiring ?? 0,
        icon: FileText,
        route: ROUTES.quotations,
        severity: 'warning',
        urgencyCopy: 'Requires price revision or closure',
      },
      {
        id: 'edit-approvals',
        label: 'Invoice Modification Approvals',
        count: attention.editApprovals ?? 0,
        icon: CheckSquare,
        route: ROUTES.invoiceEditApprovals,
        severity: 'warning',
        urgencyCopy: 'Finance authorization required',
      },
      {
        id: 'draft-invoices',
        label: 'Pending Draft Invoices',
        count: attention.draftInvoices ?? 0,
        icon: FilePen,
        route: ROUTES.invoices,
        severity: 'warning',
        urgencyCopy: 'Awaiting final posting & dispatch',
      },
      {
        id: 'samples-dispatch',
        label: 'Product Samples Awaiting Dispatch',
        count: dispatchMetrics?.samplesToDispatch ?? 0,
        icon: FlaskConical,
        route: ROUTES.sampling,
        severity: 'info',
        urgencyCopy: 'Courier packaging pending',
      },
      {
        id: 'samples-feedback',
        label: 'Sample Evaluations Pending Feedback',
        count: attention.samplesAwaitingFeedback ?? 0,
        icon: FlaskConical,
        route: ROUTES.sampling,
        severity: 'info',
        urgencyCopy: 'Customer laboratory trial response due',
      },
    ];
    return list.filter((item) => item.count > 0);
  }, [attention, dispatchMetrics]);

  // One card chrome for the whole console: hairline border, 12px radius, one lift.
  const surfaceCard = 'overflow-hidden rounded-card border border-border bg-card shadow-surface-1';

  return (
    <div className="mx-auto max-w-[1600px] space-y-4 pb-16 font-sans text-on-surface">
      {/* ── Page header: the shared WelcomeBanner — identity, scope and the one
          control that changes view (Workforce / My workspace) as its action. ── */}
      <WelcomeBanner
        subtitle={(
          <>
            <span className="mr-2 inline-block rounded-lg bg-card/80 px-2 py-0.5 text-data-xs font-medium text-on-surface-variant">General Manager &amp; Sales Administrator</span>
            {activeMode === 'workforce'
              ? 'All personnel, commercial pipeline, fulfilment and activity logs.'
              : 'Your direct accounts, pipeline opportunities and key-account quotations.'}
          </>
        )}
        actions={(
          <nav aria-label="Primary operational modes" className="flex items-center gap-1 rounded-card-sm border border-border bg-card/80 p-1 backdrop-blur">
            {([
              { key: 'workforce' as const, label: 'Workforce operations', hint: 'All personnel and operations', icon: Users },
              { key: 'personal' as const, label: 'My workspace', hint: 'Lead BDM direct accounts', icon: Briefcase },
            ]).map((m) => (
              <button
                key={m.key}
                type="button"
                aria-pressed={activeMode === m.key}
                onClick={() => setActiveMode(m.key)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors ${
                  activeMode === m.key
                    ? 'bg-primary-container text-primary-dim shadow-xs ring-1 ring-primary/25'
                    : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                }`}
              >
                <m.icon className="h-4 w-4" aria-hidden />
                <span className="text-left leading-tight">
                  <span className="block">{m.label}</span>
                  <span className="block text-data-xs text-on-surface-subtle">{m.hint}</span>
                </span>
              </button>
            ))}
          </nav>
        )}
      />

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* MODE 1: ENTERPRISE WORKFORCE & OPERATIONS MONITORING               */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {activeMode === 'workforce' && (
        <main className="space-y-5">
          {/* ── Operational Attention & Triage Strip ───────────────────────── */}
          {operationalAlerts.length > 0 && (
            <section aria-label="Operational action items" className="rounded-card border border-border bg-card p-4 shadow-xs">
              <div className="mb-2.5 flex items-center justify-between gap-3 border-b border-border pb-2.5">
                <div className="flex items-center gap-2 text-body-sm font-semibold text-on-surface">
                  <AlertTriangle className="h-4 w-4 text-danger-text" aria-hidden />
                  <span>Needs your action</span>
                </div>
                <span className="font-mono text-data-xs text-data-sm tabular-nums text-on-surface-variant">
                  {operationalAlerts.reduce((sum, item) => sum + item.count, 0)} items
                </span>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {operationalAlerts.map((item) => (
                  <Link
                    key={item.id}
                    to={item.route}
                    className="group flex min-w-0 items-center justify-between gap-2 rounded-card-sm border border-border bg-surface-variant px-3 py-2.5 transition-colors hover:bg-surface-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <div
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                          item.severity === 'critical'
                            ? 'bg-danger/10 text-danger-text'
                            : item.severity === 'warning'
                            ? 'bg-warning/10 text-warning-text'
                            : 'bg-info/10 text-info-text'
                        }`}
                        aria-hidden
                      >
                        <item.icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-body-sm font-medium text-on-surface">{item.label}</div>
                        <div className="truncate text-data-xs text-on-surface-variant">{item.urgencyCopy}</div>
                      </div>
                    </div>
                    <div className="ml-1 flex shrink-0 items-center gap-1">
                      <span className="font-mono text-data-md font-semibold text-data-sm tabular-nums text-on-surface">{item.count}</span>
                      <ChevronRight className="h-3.5 w-3.5 text-on-surface-subtle transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* ── Sub-Navigation & Reporting Period Selector ─────────────────── */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-border bg-card p-2.5 shadow-xs">
            {/* Operational Dimensions */}
            <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Monitoring Dimensions">
              <button
                type="button"
                role="tab"
                aria-selected={monitoringTab === 'scoreboard'}
                onClick={() => setMonitoringTab('scoreboard')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors ${
                  monitoringTab === 'scoreboard'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <Users className="h-3.5 w-3.5" />
                Workforce roster
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={monitoringTab === 'pipeline'}
                onClick={() => setMonitoringTab('pipeline')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors ${
                  monitoringTab === 'pipeline'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <BarChart3 className="h-3.5 w-3.5" />
                Pipeline monitor
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={monitoringTab === 'dsr'}
                onClick={() => {
                  setMonitoringTab('dsr');
                  setSelectedRep(null);
                }}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors ${
                  monitoringTab === 'dsr'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <FileText className="h-3.5 w-3.5" />
                Daily sales report
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={monitoringTab === 'dispatch'}
                onClick={() => setMonitoringTab('dispatch')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors ${
                  monitoringTab === 'dispatch'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <Truck className="h-3.5 w-3.5" />
                Dispatch
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={monitoringTab === 'commercial'}
                onClick={() => setMonitoringTab('commercial')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors ${
                  monitoringTab === 'commercial'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <Package className="h-3.5 w-3.5" />
                Accounts and volume
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={monitoringTab === 'gps'}
                onClick={() => setMonitoringTab('gps')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors ${
                  monitoringTab === 'gps'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <Radio className="h-3.5 w-3.5" />
                Live GPS
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={monitoringTab === 'analytics'}
                onClick={() => setMonitoringTab('analytics')}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-body-sm font-medium transition-colors ${
                  monitoringTab === 'analytics'
                    ? 'bg-primary text-on-primary shadow-xs'
                    : 'text-on-surface-variant hover:bg-surface-container'
                }`}
              >
                <TrendingUp className="h-3.5 w-3.5" />
                Sales rankings
              </button>
            </div>

            {/* Standard Period Selection */}
            <div className="flex items-center gap-1 rounded-lg border border-border bg-surface-variant p-1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPeriodKey(p.key)}
                  className={`rounded-lg px-3 py-1 text-body-sm font-semibold transition ${
                    periodKey === p.key
                      ? 'bg-card text-primary-dim font-semibold shadow-xs'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Executive Operational KPI Overview ─────────────────────────── */}
          {/* One tile shape for the whole console: figure first (mono, tabular,
              India-grouped), label and context beneath. */}
          <section aria-label="Executive KPI overview" className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            {([
              {
                label: 'Sales personnel',
                value: String(teamMembers.length),
                sub: `${inDutyCount} on duty · ${checkedOutCount} departed · ${absentCount} off`,
              },
              {
                label: 'Inbound leads',
                value: String(leadMetrics?.created ?? 0),
                sub: `${leadMetrics?.converted ?? 0} converted · ${leadMetrics?.enquiries?.waitingToBeAssigned ?? 0} unassigned`,
                tone: leadMetrics?.conversionRate ? `text-success-text ${leadMetrics.conversionRate}% win rate` : undefined,
              },
              {
                label: 'Open pipeline',
                value: formatINR(leadMetrics?.openPipeline?.value ?? 0),
                sub: `${leadMetrics?.openPipeline?.count ?? 0} active opportunities`,
              },
              {
                label: 'Quotations issued',
                value: formatINR(moneyMetrics?.quotations?.value ?? 0),
                sub: `${moneyMetrics?.quotations?.count ?? 0} proposals`,
              },
              {
                label: 'Invoiced revenue',
                value: formatINR(moneyMetrics?.sales?.value ?? 0),
                sub: `Collected ${formatINR(moneyMetrics?.collected?.value ?? 0)}`,
                tone: 'text-success-text',
              },
              {
                label: 'Accounts receivable',
                value: formatINR(moneyMetrics?.receivables?.total ?? 0),
                sub: `Overdue ${formatINR(moneyMetrics?.receivables?.overdue ?? 0)}`,
                tone: 'text-danger-text',
              },
            ]).map((tile, i) => {
              const look: [typeof UsersIcon, BannerTone][] = [
                [UsersIcon, 'primary'], [InboxIcon, 'info'], [TargetIcon, 'primary'],
                [FileTextIcon, 'warning'], [ReceiptIcon, 'success'], [LandmarkIcon, 'danger'],
              ];
              const [icon, tone] = look[i] || [TargetIcon, 'primary'];
              return (
                <StatTile key={tile.label} stat={{
                  label: tile.label, value: tile.value, icon, tone,
                  sub: tile.sub ? <span className={tile.tone || ''} title={tile.sub}>{tile.sub}</span> : undefined,
                }} />
              );
            })}
          </section>

          {/* ── Sub-Tab 1: Workforce Performance Roster & Attendance ───────── */}
          {monitoringTab === 'scoreboard' && (
            <div className="space-y-5">
              {/* Master Personnel Matrix */}
              <section className={surfaceCard} aria-label="Workforce performance roster">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                  <div className="min-w-0">
                    <h2 className="text-headline-sm font-semibold text-on-surface">Workforce roster</h2>
                    <p className="text-body-sm text-on-surface-variant">
                      {range.from === range.to ? range.from : `${range.from} to ${range.to}`} · all active representatives
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-data-xs text-on-surface-variant">
                    <span className="inline-block h-2 w-2 rounded-full bg-success" aria-hidden />
                    <span>Reflects today&rsquo;s attendance</span>
                  </div>
                </div>

                <div className="max-h-[600px] overflow-auto p-4">
                  {isDataLoading ? (
                    <WidgetLoading variant="table" />
                  ) : isDataError ? (
                    <WidgetError message="The team numbers could not be read." />
                  ) : teamMembers.length === 0 ? (
                    <WidgetEmpty message={`No one reported against ${range.from === range.to ? range.from : `${range.from} to ${range.to}`}. Shorten the period or check the department.`} />
                  ) : (
                    <table className="w-full text-body-sm">
                      <thead>
                        <tr className="text-left text-label-caps uppercase tracking-wider text-on-surface-variant">
                          <th scope="col" className="sticky top-0 z-10 bg-muted py-2.5 pr-3">Representative</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-center">Duty status</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-right">Leads</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-right">Pipeline</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-right">Quotations</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-right">Invoiced</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-right">Collected</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-right">Quota</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-right">Overdue</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted px-2 py-2.5 text-right">Last activity</th>
                          <th scope="col" className="sticky top-0 z-10 bg-muted py-2.5 pl-3 text-center">Audit</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {teamMembers.map((rep) => {
                          const attendance = attendanceList.find(
                            (a) => a.id === rep.userId || a.display_name === rep.name
                          );
                          const isOnline = attendance?.check_in && !attendance?.check_out;
                          const targetPct = rep.monthTarget > 0 ? Math.round((rep.sales / rep.monthTarget) * 100) : null;
                          const isCurrentAccount = rep.userId === String(user?.id) || /suraj/i.test(rep.name);

                          return (
                            <tr
                              key={rep.userId}
                              className={`transition-colors hover:bg-muted/50 ${
                                isCurrentAccount ? 'bg-primary-container' : ''
                              }`}
                            >
                              {/* Member info */}
                              <td className="py-2.5 pr-3">
                                <div className="flex items-center gap-1.5 font-medium text-on-surface">
                                  <span>{rep.name}</span>
                                  {isCurrentAccount && (
                                    <span className="rounded-lg bg-primary-container px-1.5 py-0.5 text-label-caps uppercase tracking-wider text-primary-dim">
                                      You
                                    </span>
                                  )}
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-data-xs text-on-surface-variant">
                                  {rep.designation && <span className="font-medium text-on-surface-variant">{rep.designation}</span>}
                                  {rep.role && <span className="capitalize text-on-surface-subtle">({rep.role.replace('_', ' ')})</span>}
                                  {rep.phone && (
                                    <a
                                      href={`tel:${rep.phone}`}
                                      className="inline-flex items-center gap-1 font-mono text-primary hover:underline"
                                      title={`Call ${rep.name}`}
                                    >
                                      <Phone className="h-2.5 w-2.5" />
                                      <span>{rep.phone}</span>
                                    </a>
                                  )}
                                </div>
                              </td>

                              {/* Duty Status */}
                              <td className="px-2 py-2.5 text-center">
                                {isOnline ? (
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-2 py-0.5 text-data-xs font-medium text-success-text">
                                    <span className="h-1.5 w-1.5 rounded-full bg-success motion-reduce:animate-none" />
                                    On duty since {attendance?.check_in ? formatClock(attendance.check_in) : '—'}
                                  </span>
                                ) : attendance?.check_out ? (
                                  <span className="inline-flex items-center rounded-full bg-surface-container px-2 py-0.5 text-data-xs font-medium text-on-surface-variant">
                                    Left {formatClock(attendance.check_out)}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center rounded-full bg-surface-variant px-2 py-0.5 text-data-xs font-medium text-on-surface-subtle">
                                    Not in
                                  </span>
                                )}
                              </td>

                              {/* Inbound Leads */}
                              <td className="px-2 py-2.5 text-right font-mono text-data-sm font-semibold tabular-nums text-on-surface">
                                {rep.leadsNew}
                              </td>

                              {/* Active Pipeline */}
                              <td className="px-2 py-2.5 text-right font-mono text-data-sm tabular-nums">
                                <span className="font-semibold text-on-surface">{rep.leadsOpen}</span>{' '}
                                <span className="text-data-xs text-on-surface-subtle">· {formatINR(rep.pipelineValue)}</span>
                              </td>

                              {/* Quotations */}
                              <td className="px-2 py-2.5 text-right font-mono text-data-sm tabular-nums">
                                <span className="font-semibold text-on-surface">{rep.quotations}</span>{' '}
                                <span className="text-data-xs text-on-surface-subtle">· {formatINR(rep.quotationValue)}</span>
                              </td>

                              {/* Invoiced Sales */}
                              <td className="px-2 py-2.5 text-right font-mono text-data-sm font-semibold tabular-nums text-primary-dim">
                                {formatINR(rep.sales)}
                              </td>

                              {/* Realized Collections */}
                              <td className="px-2 py-2.5 text-right font-mono text-data-sm font-semibold tabular-nums text-success-text">
                                {formatINR(rep.collected)}
                              </td>

                              {/* Target Progress Bar */}
                              <td className="px-2 py-2.5 text-right font-mono text-data-sm tabular-nums">
                                {targetPct === null ? (
                                  <span className="text-data-xs text-on-surface-subtle">Not set</span>
                                ) : (
                                  <div className="flex flex-col items-end">
                                    <span
                                      className={`font-mono text-data-sm font-semibold tabular-nums ${
                                        targetPct >= 100
                                          ? 'text-success-text'
                                          : targetPct >= 50
                                          ? 'text-primary'
                                          : 'text-on-surface-variant'
                                      }`}
                                    >
                                      {targetPct}%
                                    </span>
                                    <div className="mt-1 h-1.5 w-16 overflow-hidden rounded-full bg-surface-variant">
                                      <div
                                        className={`h-full rounded-full ${
                                          targetPct >= 100 ? 'bg-success' : targetPct >= 50 ? 'bg-primary/40' : 'bg-warning'
                                        }`}
                                        style={{ width: `${Math.min(100, targetPct)}%` }}
                                      />
                                    </div>
                                    <span className="mt-0.5 font-mono text-data-xs tabular-nums text-on-surface-subtle">{formatINR(rep.monthTarget)}</span>
                                  </div>
                                )}
                              </td>

                              {/* Overdue Items */}
                              <td
                                className={`px-2 py-2.5 text-right font-mono text-data-sm font-semibold tabular-nums ${
                                  rep.overdueFollowups > 0 ? 'text-danger-text' : 'text-on-surface-subtle'
                                }`}
                              >
                                {rep.overdueFollowups > 0 ? rep.overdueFollowups : '—'}
                              </td>

                              {/* Last Activity */}
                              <td className="font-mono px-2 py-2.5 text-right text-data-xs text-on-surface-variant tabular-nums text-data-sm">
                                {formatTimeAgo(rep.lastSeenAt)}
                              </td>

                              {/* Direct Inspection Action */}
                              <td className="py-2.5 pl-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMonitoringTab('dsr');
                                    setSelectedRep({ id: rep.userId, name: rep.name });
                                  }}
                                  className="rounded-lg border border-border bg-card px-2.5 py-1 text-data-xs font-semibold text-on-surface-variant hover:bg-primary-container hover:border-primary/30 hover:text-primary shadow-xs transition"
                                >
                                  Audit DSR
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>

              {/* Real-Time Attendance Roster */}
              <section className={surfaceCard} aria-label="Attendance today">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                  <div>
                    <h2 className="text-headline-sm font-semibold text-on-surface">Attendance today</h2>
                    <p className="text-body-sm text-on-surface-variant">Check-in, check-out and time on duty as recorded today.</p>
                  </div>
                  <span className="rounded-lg border border-success/30 bg-success/10 px-2.5 py-1 text-data-xs font-medium text-success-text">
                    {inDutyCount} on duty
                  </span>
                </div>

                <div className="overflow-x-auto p-4">
                  <table className="w-full text-body-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-data-xs font-semibold uppercase text-on-surface-subtle">
                        <th className="py-2.5 pr-2">Personnel Identifier</th>
                        <th className="px-2 py-2.5">Check-In Timestamp</th>
                        <th className="px-2 py-2.5">Departure Timestamp</th>
                        <th className="px-2 py-2.5 text-right">Logged Duty Duration</th>
                        <th className="py-2.5 pl-2 text-right">Current Operational Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attList.visible.map((record) => (
                        <tr key={record.id} className="border-b border-border hover:bg-surface-variant/50">
                          <td className="py-2.5 pr-2 font-semibold text-on-surface">{record.display_name}</td>
                          <td className="px-2 py-2.5 font-mono text-data-sm tabular-nums text-on-surface-variant">
                            {record.check_in ? formatClock(record.check_in) : '—'}
                          </td>
                          <td className="px-2 py-2.5 font-mono text-data-sm tabular-nums text-on-surface-variant">
                            {record.check_out ? formatClock(record.check_out) : '—'}
                          </td>
                          <td className="px-2 py-2.5 text-right font-mono text-data-sm font-semibold tabular-nums text-on-surface-variant">
                            {record.check_in ? formatDuration(minutesOnDuty(record.check_in, record.check_out)) : '—'}
                          </td>
                          <td className="font-mono py-2.5 pl-2 text-right font-semibold tabular-nums text-data-sm">
                            {!record.check_in ? (
                              <span className="rounded-lg bg-warning/10 px-2 py-0.5 text-data-xs font-medium text-warning-text">Not in</span>
                            ) : record.check_out ? (
                              <span className="rounded-lg bg-info/10 px-2 py-0.5 text-data-xs font-medium text-info-text">Checked out</span>
                            ) : (
                              <span className="rounded-lg bg-success/10 px-2 py-0.5 text-data-xs font-medium text-success-text">On duty</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 pb-4"><ShowAllButton hidden={attList.hidden} all={attList.all} onToggle={attList.toggle} /></div>
                </div>
              </section>
            </div>
          )}

          {/* ── Sub-Tab 2: Pipeline Opportunity Matrix ─────────────────────── */}
          {monitoringTab === 'pipeline' && (
            <div className="space-y-4">
              {monitorAllowed ? (
                <div className={`${surfaceCard} p-5`}>
                  <EmployeePipelineMonitor
                    summaryData={repSummary}
                    isLoading={isRepSummaryLoading}
                    selectedOwnerId={monitorOwner}
                    onSelectOwner={setMonitorOwner}
                    userRole={user?.role}
                  />
                </div>
              ) : (
                <div className={`${surfaceCard} p-4`}>
                  <WidgetPermissionDenied
                    title="Pipeline monitor needs sales-wide access"
                    detail="Your role does not carry the pipeline workspace, so no representative figures are shown here — this is not an empty week."
                  />
                </div>
              )}
            </div>
          )}

          {/* ── Sub-Tab 3: Daily Sales Activity (DSR) ──────────────────────── */}
          {monitoringTab === 'dsr' && (
            <div className="space-y-5">
              <AsmTeamDsrCard onSelectRep={(id, name) => setSelectedRep({ id, name })} />

              {selectedRep && (
                <div className="rounded-card border border-border bg-card p-4 shadow-xs">
                  <div className="mb-3 flex items-center justify-between gap-2 border-b border-border pb-2.5">
                    <span className="text-body-sm font-semibold text-on-surface">
                      Daily sales activity · {selectedRep.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setSelectedRep(null)}
                      className="rounded-lg border border-border bg-card px-2.5 py-1 text-body-sm font-medium text-on-surface-variant transition-colors hover:bg-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      Close
                    </button>
                  </div>
                  <BdmDsrWidget userId={selectedRep.id} repName={selectedRep.name} />
                </div>
              )}
            </div>
          )}

          {/* ── Sub-Tab 4: Dispatch & Fulfillment Radar ────────────────────── */}
          {monitoringTab === 'dispatch' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className={`${surfaceCard} flex flex-col gap-1 border-l-4 border-l-warning p-4`}>
                  <div className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">
                    {dispatchMetrics?.awaiting.count ?? 0} <span className="text-data-xs font-normal text-on-surface-subtle">orders</span>
                  </div>
                  <div className="text-body-sm text-on-surface-variant">Awaiting dispatch</div>
                  <div className="font-mono text-data-md tabular-nums text-warning-text">
                    {formatINR(dispatchMetrics?.awaiting.value ?? 0)}
                  </div>
                  <p className="mt-1 text-data-xs text-on-surface-subtle">Packaged, pending carrier assignment or transport departure.</p>
                </div>

                <div className={`${surfaceCard} flex flex-col gap-1 border-l-4 border-l-danger p-4`}>
                  <div className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">
                    {dispatchMetrics?.overdue.count ?? 0} <span className="text-data-xs font-normal text-on-surface-subtle">orders</span>
                  </div>
                  <div className="text-body-sm text-on-surface-variant">Overdue deliveries</div>
                  <div className="font-mono text-data-md tabular-nums text-danger-text">
                    {formatINR(dispatchMetrics?.overdue.value ?? 0)}
                  </div>
                  <p className="mt-1 text-data-xs text-on-surface-subtle">Past the fulfilment window. Expedite or re-date.</p>
                </div>

                <div className={`${surfaceCard} flex flex-col gap-1 border-l-4 border-l-success p-4`}>
                  <div className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">
                    {dispatchMetrics?.dispatched.count ?? 0} <span className="text-data-xs font-normal text-on-surface-subtle">orders</span>
                  </div>
                  <div className="text-body-sm text-on-surface-variant">Dispatched in period</div>
                  <div className="font-mono text-data-md tabular-nums text-success-text">
                    {formatINR(dispatchMetrics?.dispatched.value ?? 0)}
                  </div>
                  <p className="mt-1 text-data-xs text-on-surface-subtle">Handed to the carrier with LR / e-Way documents.</p>
                </div>
              </div>

              <div className={`${surfaceCard} p-4 text-center`}>
                <h3 className="text-body-md font-semibold text-on-surface">Logistics and transport register</h3>
                <p className="mt-1 text-body-sm text-on-surface-variant">
                  Inspect vehicle numbers, local transport splits, LR/e-way bills, and Proof of Delivery (POD) attachments.
                </p>
                <div className="mt-4 flex justify-center gap-3">
                  <Link
                    to={ROUTES.dispatchOrders}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim transition"
                  >
                    Open Dispatch Orders Queue
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  <Link
                    to="/dispatch/local-transport"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border-strong bg-card px-4 py-2 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-variant transition"
                  >
                    View Local Transport Register
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* ── Sub-Tab 5: Client Accounts & Product Volume Analytics ──────── */}
          {monitoringTab === 'commercial' && (
            <div className="space-y-5">
              {/* Volume summary ribbon */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className={`${surfaceCard} flex flex-col gap-1 border-l-4 border-l-primary p-4`}>
                  <div className="flex items-center justify-between text-label-caps uppercase tracking-wider text-on-surface-variant">
                    <span>Dispatched volume</span>
                    <Package className="h-4 w-4 text-primary" />
                  </div>
                  <div className="mt-1 font-mono text-data-lg font-semibold tabular-nums text-on-surface">
                    {(customerMetrics?.litresSold ?? 0).toLocaleString('en-IN')} <span className="text-data-xs text-on-surface-subtle">LTR</span>
                  </div>
                  <p className="mt-1 text-data-xs text-on-surface-subtle">Litres billed and shipped in this period.</p>
                </div>

                <div className={`${surfaceCard} flex flex-col gap-1 border-l-4 border-l-success p-4`}>
                  <div className="flex items-center justify-between text-label-caps uppercase tracking-wider text-on-surface-variant">
                    <span>Key accounts</span>
                    <Building className="h-4 w-4 text-success-text" />
                  </div>
                  <div className="mt-1 font-mono text-data-lg font-semibold tabular-nums text-on-surface">
                    {customerMetrics?.topCustomers?.length ?? 0} <span className="text-data-xs text-on-surface-subtle">key accounts</span>
                  </div>
                  <p className="mt-1 text-data-xs text-on-surface-subtle">Accounts with the highest invoiced value.</p>
                </div>

                <div className={`${surfaceCard} flex flex-col gap-1 border-l-4 border-l-warning p-4`}>
                  <div className="flex items-center justify-between text-label-caps uppercase tracking-wider text-on-surface-variant">
                    <span>Products moved</span>
                    <Layers className="h-4 w-4 text-warning-text" />
                  </div>
                  <div className="mt-1 font-mono text-data-lg font-semibold tabular-nums text-on-surface">
                    {customerMetrics?.topProducts?.length ?? 0} <span className="text-data-xs text-on-surface-subtle">products</span>
                  </div>
                  <p className="mt-1 text-data-xs text-on-surface-subtle">Products with the highest invoiced value.</p>
                </div>
              </div>

              {/* Top Customers & Top Products Tables */}
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Principal Corporate Accounts */}
                <section className={surfaceCard} aria-label="Top accounts by invoiced value">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-primary" />
                      <h3 className="text-body-sm font-semibold text-on-surface">Top accounts by invoiced value</h3>
                    </div>
                    <span className="text-data-xs text-on-surface-subtle">This period</span>
                  </div>
                  <div className="overflow-x-auto p-4">
                    {customerMetrics?.topCustomers && customerMetrics.topCustomers.length > 0 ? (
                      <table className="w-full text-body-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-data-xs font-semibold uppercase text-on-surface-subtle">
                            <th className="pb-2.5 pr-2">Client Organization</th>
                            <th className="pb-2.5 px-2 text-center">Invoices Count</th>
                            <th className="pb-2.5 pl-2 text-right">Turnover Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerMetrics.topCustomers.map((c) => (
                            <tr key={c.customerId} className="border-b border-border hover:bg-surface-variant/60">
                              <td className="py-2.5 pr-2 font-semibold text-on-surface">{c.name}</td>
                              <td className="py-2.5 px-2 text-center tabular-nums font-semibold text-on-surface-variant">
                                {c.invoices}
                              </td>
                              <td className="py-2.5 pl-2 text-right font-mono text-data-sm font-semibold tabular-nums text-primary-dim">
                                {formatINR(c.value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-6 text-center text-body-sm text-on-surface-variant">No account has an invoice in this period. Pick a wider window.</div>
                    )}
                  </div>
                </section>

                {/* Leading Product Formulations */}
                <section className={surfaceCard} aria-label="Top products by volume">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-warning-text" />
                      <h3 className="text-body-sm font-semibold text-on-surface">Top products by volume</h3>
                    </div>
                    <span className="text-data-xs text-on-surface-subtle">This period</span>
                  </div>
                  <div className="overflow-x-auto p-4">
                    {customerMetrics?.topProducts && customerMetrics.topProducts.length > 0 ? (
                      <table className="w-full text-body-sm">
                        <thead>
                          <tr className="border-b border-border text-left text-data-xs font-semibold uppercase text-on-surface-subtle">
                            <th className="pb-2.5 pr-2">Product Formulation</th>
                            <th className="pb-2.5 px-2 text-right">Dispatched Litres</th>
                            <th className="pb-2.5 pl-2 text-right">Invoiced Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerMetrics.topProducts.map((p, idx) => (
                            <tr key={idx} className="border-b border-border hover:bg-surface-variant/60">
                              <td className="py-2.5 pr-2 font-semibold text-on-surface">{p.name}</td>
                              <td className="py-2.5 px-2 text-right font-mono text-data-sm font-semibold tabular-nums text-on-surface-variant">
                                {p.litres > 0 ? `${p.litres.toLocaleString('en-IN')} LTR` : `${p.quantity} units`}
                              </td>
                              <td className="py-2.5 pl-2 text-right font-mono text-data-sm font-semibold tabular-nums text-success-text">
                                {formatINR(p.value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-6 text-center text-body-sm text-on-surface-variant">Nothing was billed in this period. Pick a wider window.</div>
                    )}
                  </div>
                </section>
              </div>

              {/* Lead Source Breakdown if available */}
              {leadMetrics?.bySource && leadMetrics.bySource.length > 0 && (
                <section className={surfaceCard} aria-label="Inbound Lead Acquisition Channels">
                  <div className="border-b border-border px-4 py-2.5">
                    <h3 className="text-body-sm font-semibold text-on-surface">Where the leads came from</h3>
                    
                  </div>
                  <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6">
                    {leadMetrics.bySource.map((s) => (
                      <div key={s.source} className="rounded-lg border border-border bg-surface-variant/80 p-3">
                        <div className="text-data-xs font-semibold uppercase tracking-wider text-on-surface-variant truncate">
                          {s.source.replace('_', ' ')}
                        </div>
                        <div className="mt-1 font-mono text-data-lg font-semibold tabular-nums text-on-surface">{s.count}</div>
                        <div className="text-data-xs text-on-surface-subtle">leads</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* ── Sub-Tab 6: Fleet Tracking & Live GPS Radar ──────────────────── */}
          {monitoringTab === 'gps' && (
            <div className={`${surfaceCard} p-2 overflow-hidden`}>
              <LiveFleetRadarPage />
            </div>
          )}

          {/* ── Sub-Tab 7: Sales Velocity & Deal Rankings ──────────────────── */}
          {monitoringTab === 'analytics' && (
            <div className="space-y-5">
              <SalesAdminDashboard />
            </div>
          )}
        </main>
      )}

      {/* ────────────────────────────────────────────────────────────────── */}
      {/* MODE 2: PERSONAL EXECUTIVE WORKSPACE (LEAD BDM 1)                  */}
      {/* ────────────────────────────────────────────────────────────────── */}
      {activeMode === 'personal' && (
        <section aria-label="Personal Executive Accounts" className="space-y-4">
          <div className="flex items-center justify-between rounded-card border border-primary/30 bg-primary-container px-5 py-2.5 text-body-sm text-primary-dim">
            <div className="flex items-center gap-2.5">
              <Sparkles className="h-4 w-4 text-primary" />
              <span>
                <strong>Executive Account Portfolio:</strong> Dedicated cockpit managing client accounts, pipeline opportunities, commercial quotations, and field visits assigned directly to Suraj Mishra.
              </span>
            </div>
            <button
              type="button"
              onClick={() => setActiveMode('workforce')}
              className="font-semibold text-primary-dim hover:underline"
            >
              Switch to Workforce Operations Console →
            </button>
          </div>

          <SalesEmployeeDashboard />
        </section>
      )}
    </div>
  );
}
